import React, { useCallback, useMemo } from 'react';

import classnames from 'clsx';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import { Checkbox } from 'ui-components/components/checkbox/';
import { noop } from 'ui-components/default-props/noop';

import BillToAccountDropdown from './bill-to-account-dropdown';
import BillToAccountExtra from './bill-to-account-extra';
import { BILL_OPTIONS } from './constants/bill-to-account-options';
import { emptyArray } from 'ui-components/default-props/empty';
import { RECIPIENT, THIRD_PARTY, COLLECT } from '../../../../../enums/bill-to-party';
import { Providers } from '../../../../../enums/providers';
import messages from '../../../messages';
import { useOtherShippingOptionsStyleContext } from '../../../style-provider';

import style from '../inputs.module.less';

export const getShouldShowBillingAccount = (isBulkUpdate, isLabelProviderSelected) => {
  return isBulkUpdate || isLabelProviderSelected;
};

const shouldShowExtraInputs = (activeParty, selectedSellerLabelProviderId) => {
  return ![selectedSellerLabelProviderId, COLLECT].includes(activeParty);
};

const EMPTY_BILL_OPTIONS = Object.keys(BILL_OPTIONS).reduce(
  (results, option) => ({
    ...results,
    [option]: '',
  }),
  {},
);

const thirdPartyBillingOptions = [RECIPIENT, THIRD_PARTY];

const displayTextMap = {
  [RECIPIENT]: <FormattedMessage {...messages.recipient} />,
  [THIRD_PARTY]: <FormattedMessage {...messages.thirdParty} />,
  [COLLECT]: <FormattedMessage {...messages.collect} />,
};

const BillToAccountOption = ({
  activeParty = '',
  canUseFedExGroundCollect = false,
  carrierId = '',
  checked,
  isBulkUpdate = false,
  onCheck = noop,
  optionData = null,
  selectedSellerLabelProviderId = '',
  sellerLabelProviders = emptyArray,
  thirdPartyBillingIsAllowed = false,
  updateContext = null,
  updateInput = noop,
  updateOption = noop,
}) => {
  const intl = useIntl();
  const { classes } = useOtherShippingOptionsStyleContext();
  const checkBoxLabel = intl.formatMessage(messages.default);
  const formattedSellerLabelProviders = useMemo(
    () =>
      sellerLabelProviders.map((slp) =>
        slp.isPrimaryAccount ? { ...slp, name: intl.formatMessage(messages.primaryAccount, { accountName: slp.name }) } : slp,
      ),
    [intl, sellerLabelProviders],
  );

  const billToAccountOptions = useMemo(() => {
    let options = formattedSellerLabelProviders.reduce((results, p) => {
      const result = {
        id: p.sellerLabelProviderId,
        name: p.name,
        value: p.sellerLabelProviderId,
      };

      // UPS_ONE_BALANCE_PROVIDERID should always be first in the drop-down options
      if (carrierId === Providers.UPS.labelProviderId) {
        if (p.labelProviderId === Providers.UPSWallet.labelProviderId) {
          return [result, ...results];
        }
      }

      return [...results, result];
    }, []);

    if (thirdPartyBillingIsAllowed) {
      const thirdPartyOptions = thirdPartyBillingOptions.map((name) => ({
        name: displayTextMap[name],
        value: name,
      }));
      options = [...options, ...thirdPartyOptions];
    }

    if (canUseFedExGroundCollect) {
      options = [
        ...options,
        {
          name: displayTextMap.Collect,
          value: COLLECT,
        },
      ];
    }

    return options;
  }, [canUseFedExGroundCollect, carrierId, formattedSellerLabelProviders, thirdPartyBillingIsAllowed]);

  const isStatic =
    sellerLabelProviders.length === 1 &&
    sellerLabelProviders[0].sellerLabelProviderId === selectedSellerLabelProviderId &&
    !thirdPartyBillingIsAllowed;

  const addToBillToAccountObject = useCallback(
    (field, value) => {
      const currentBillTo = optionData || EMPTY_BILL_OPTIONS;
      return {
        ...currentBillTo,
        [field]: value,
      };
    },
    [optionData],
  );

  const updateBillToAccount = useCallback(
    (field, value, sellerLabelProviderId) => {
      const newValue = {
        id: 'billToAccount',
        sellerLabelProviderId: sellerLabelProviderId || selectedSellerLabelProviderId,
        value: sellerLabelProviderId ? undefined : addToBillToAccountObject(field, value),
      };

      updateOption(newValue);
    },
    [addToBillToAccountObject, selectedSellerLabelProviderId, updateOption],
  );

  const updateNonExtraBillToAccount = useCallback(
    (field, value, sellerLabelProviderId) => {
      updateBillToAccount(field, value, sellerLabelProviderId);
      if (updateContext && sellerLabelProviderId) {
        updateContext(sellerLabelProviderId);
      }
    },
    [updateBillToAccount, updateContext],
  );

  const updateInputValue = (field, v) => {
    updateInput({
      id: 'billToAccount',
      value: addToBillToAccountObject(field, v),
    });
  };

  const bulkModalOnCheck = useCallback(() => {
    onCheck('billToAccount');
  }, [onCheck]);

  const renderBillingInputs = () => {
    const { billToParty, ...billExtraOptions } = optionData || {};
    const showExtraInputs = shouldShowExtraInputs(activeParty, selectedSellerLabelProviderId);

    return (
      <div className={style['bill-to-account']}>
        <div className={classnames(style['bill-to-row'], classes.billToRow)}>
          <label className={classes.sectionTitle}>
            <FormattedMessage defaultMessage='Shipping Account' description='otherShippingOptions.billTo.Title' id='Zsbcao' />
          </label>
          <BillToAccountDropdown
            activeParty={activeParty}
            field='billToParty'
            isStatic={isStatic}
            options={billToAccountOptions}
            updateBillToAccount={updateNonExtraBillToAccount}
          />
        </div>
        {showExtraInputs && (
          <BillToAccountExtra
            isBulkUpdate={isBulkUpdate}
            options={billExtraOptions}
            updateBillToAccount={updateBillToAccount}
            updateInputValue={updateInputValue}
          />
        )}
      </div>
    );
  };

  const renderCheckboxStyleForBulk = () => (
    <div className={style['bill-to-account-wrapper']}>
      <Checkbox checked={checked} label={checkBoxLabel} onChange={bulkModalOnCheck} />
      {checked && renderBillingInputs()}
    </div>
  );

  const isLabelProviderSelected = !!selectedSellerLabelProviderId;
  const shouldShow = getShouldShowBillingAccount(isBulkUpdate, isLabelProviderSelected);

  if (!shouldShow) {
    return null;
  }

  if (isBulkUpdate) {
    return renderCheckboxStyleForBulk();
  }

  return <div className={style['bill-to-account-wrapper']}>{renderBillingInputs()}</div>;
};

BillToAccountOption.propTypes = {
  activeParty: PropTypes.string,
  canUseFedExGroundCollect: PropTypes.bool,
  carrierId: PropTypes.string,
  checked: PropTypes.bool.isRequired,
  isBulkUpdate: PropTypes.bool,
  onCheck: PropTypes.func,
  optionData: PropTypes.object,
  selectedSellerLabelProviderId: PropTypes.string,
  sellerLabelProviders: PropTypes.array,
  thirdPartyBillingIsAllowed: PropTypes.bool,
  updateContext: PropTypes.func,
  updateInput: PropTypes.func,
  updateOption: PropTypes.func,
};

export default BillToAccountOption;
